import { ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output, inject, DestroyRef } from '@angular/core';
import { Subscription } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { GtwElementsBaseUrlsModel } from '../../../shared/_models/gtw-elements-base-urls.model';
import { JsonObjectFactoryService } from '../../../shared/_services/json-object-factory.service';
import { F163jAutoEpService } from './f163j-auto-ep.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  gridPayloadFromActiveScreen,
  hydratedGlobalParams,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
} from '../../../shared/workspace-element-context';

@Component({
    selector: 'gtw-163j-auto-ep',
    templateUrl: './f163j-auto-ep.component.html',
    styleUrls: ['./f163j-auto-ep.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class F163jAutoEpComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  private readonly subs = new Subscription();

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();
  
  appService: any = {
    USER_SETTINGS: '',
    SERVER_CONFIG: ''
  };

  // 🔴 CRITICAL: Row data from AngularJS - NO MOCKS!
  rowData: any = {};

  // 🚀 CRITICAL: Filter params input for dynamic data (F7004 SUCCESS PATTERN)
  filterParams: any = {};

  // Component Properties
  baseURLs: GtwElementsBaseUrlsModel = {} as GtwElementsBaseUrlsModel;
  globalService: any;
  GENERAL_CONFIG: any;
  workspaceData: any[] = [];
  modalName: string = '';
  gridData: any = {};
  
  userSettings: any = this.appService.USER_SETTINGS;
  logged_in_user: string = '';
  
  title: string = "Confirm";
  batch_process: string = 'Y';
  ccKeysString: string = '';
  saveData: any[] = [];
  
  // Loading states
  loading: boolean = false;
  processing: boolean = false;

  constructor(
    private jsonObjectFactory: JsonObjectFactoryService,
    private f163jAutoEpService: F163jAutoEpService,
    private _snackBar: MatSnackBar,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.init();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    header.globalParams = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || header.globalParams || {};
    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal);
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.globalService = header;
    this.appService = {
      USER_SETTINGS: header.userSettingsData || {},
      SERVER_CONFIG: header.serverConfigData || {},
    };
    const d = activeScreen?.data;
    if (Array.isArray(d?.data)) {
      this.workspaceData = d.data;
    } else if (Array.isArray(d)) {
      this.workspaceData = d;
    } else {
      this.workspaceData = [];
    }
    this.modalName =
      d?.modalName ??
      (activeScreen as any)?.modalName ??
      '';
    const gridFromScreen = gridPayloadFromActiveScreen(activeScreen);
    this.gridData =
      gridFromScreen ??
      d?.gridData ??
      d?.grid_data ??
      d?.grid ??
      {};
    this.filterParams = normalizeFilterParams(activeScreen);
    this.rowData = rowPayloadFromActiveScreen(activeScreen);
    this.cdr.markForCheck();
  }

  init() {
    try {
      if (!this.globalService || !this.GENERAL_CONFIG) {
        return;
      }
      
      this.userSettings = this.appService.USER_SETTINGS;
      this.logged_in_user = this.userSettings?.user?.sso_id || '';
      
      // Process grid data to prepare for posting
      this.processGridData();
    } catch (e) {
      console.error('Error in F163jAutoEp init:', e);
    }
  }

  // Process grid data based on modal type
  private processGridData(): void {
    this.saveData = [];
    
    if (this.gridData != undefined && this.modalName == 'EPPosting163J') {
      // Individual selection mode
      for (var key in this.gridData) {
        this.saveData.push({
          object_id: this.gridData[key].data.object_id,
          combination_key: this.gridData[key].data.COMBINATION_KEY
        });
      }
      
      const ccKeys = this.saveData.map(item => item.combination_key);
      this.ccKeysString = ccKeys.join(',');
      this.batch_process = 'N';
    } else if (this.modalName == 'EPPosting163JBatch') {
      // Batch processing mode
      this.batch_process = 'Y';
    }
  }

  cancel() {
    this.requestGridUpdate.emit('cancel');
  }

  refreshData() {
    if (this.processing) {
      this._snackBar.open("Please wait while we save this request", 'Close', {
        duration: 4000,
        panelClass: ['warning-snackbar']
      });
      return;
    }

    this.processing = true;
    this.loading = true;

    // Build save parameters with REAL rowData values
    //const filterParams = this.globalService?.globalParams?.filterParams || {};
    let url = this.GENERAL_CONFIG.base_url + "/saveJsonObject?action_code=gqc24t";
    
    const params = {
      sso_id: this.logged_in_user,
      tax_year: this.rowData.TAX_YEAR || this.filterParams.tax_year,        // NOT globalParams.tax_year
      scenario: this.rowData.SCENARIO || this.filterParams.scenario,         // NOT hardcoded 'BASE'
      jcd_key: this.rowData.JCD_KEY || this.globalService?.globalParams?.jcd_key,  // Real JCD_KEY
      ho_leid_opt: this.filterParams.ho_leid_opt,
      combination_key: this.ccKeysString,
      batch_process: this.batch_process,
      parent_me_string: this.filterParams.parent_me_string,
      screen_key: this.globalService?.globalParams?.screen_key,
      filing_key: this.filterParams.filing_key,
      system: this.filterParams.system,
      scenarioCode: this.filterParams.scenarioCode,
      is_issue_key_enabled: this.filterParams.is_issue_key_enabled
    };

    this.f163jAutoEpService
      .postEPData(params, this.globalService, this.GENERAL_CONFIG).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        (data: any) => {
          this.processing = false;
          this.loading = false;

          if (data.callSuccess === "1") {
            this._snackBar.open("Data synced Successfully.", 'Close', {
              duration: 4000,
              panelClass: ['success-snackbar']
            });

            this.requestGridUpdate.emit('refresh');
          } else {
            this._snackBar.open(data.errorMessage || 'An error occurred during processing.', 'Close', {
              duration: 4000,
              panelClass: ['error-snackbar']
            });
            this.requestGridUpdate.emit('cancel');
          }
          this.cdr.markForCheck();
        },
        (error: any) => {
          this.processing = false;
          this.loading = false;
          this._snackBar.open('Error occurred while processing. Please try again.', 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });

          this.requestGridUpdate.emit('cancel');
          this.cdr.markForCheck();
        }
      );
  }
}